/**
 * Task Store - Manages the state and actions related to task handling in Task Page.
 *
 * @module stores/task
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useSessionStorage } from '@vueuse/core'
import { createElement } from '@/utils/jsonTextElementInterface'
import { useTapStore } from './tap'
import { useRoute } from 'vue-router'
import axios from 'axios'
import { getCaseEndTypeById, getCaseInsertTypeById } from '@/utils/docketEntryInterface'
import { useAudioPlayer } from '@/composables/useAudioPlayer'
import { formatDate } from '@/utils/datetimeUtils'
import { validateJsonTextElements } from '@/utils/jsonTextElementsSchema'
import { z } from 'zod'
import dayjs from "dayjs";

export const useTaskStore = defineStore('task', () => {
  // * =========================== State ===========================

  /**
   * Whether there is an open task.
   * @type {import('vue').Ref<boolean>}
   */
  const hasOpenTask = useSessionStorage('hasOpenTask', false)

  /**
   * Whether the task is owned by the current user.
   * @type {import('vue').Ref<'pending' | 'owned' | 'not_owned'>}
   */
  const isTaskOwnedByUser = useSessionStorage('isTaskOwnedByUser', 'pending')

  /**
   * Task data stored in session storage.
   * @type {import('vue').Ref<object>}
   */
  const taskData = useSessionStorage('taskData', {
    id: null,
    courtSession: {
      docket: {
        courtNameValue: null,
        building: null,
        room: null,
        judge: null,
        secretary: null,
        docketEntries: []
      },
      micAssignments: [],
      startAbsoluteTime: null
    },
    jsonText: { courtSessionElements: [] },
    audioStartTime: null,
    audioEndTime: null
  })

  /**
   * Audio path url for audio player component
   * @type {import('vue').Ref<string | null>}
   */
  const audioPath = useSessionStorage('audioPath', null)

  /**
   * Latest save time of the task.
   * @type {import('vue').Ref<string | null>}
   */
  const latestSaveTime = useSessionStorage('taskLatestSaveTime', null)

  /**
   * Validation errors for annotations - used in in TaskInfoDrawer.
   * @type {import('vue').Ref<Array>}
   */
  const validationErrors = ref([])

  /**
   * Merge points of 'merging' task - used in TaskInfoDrawer.
   * @type {import('vue').Ref<Array>}
   */
  const mergePoints = ref([])

  /**
   * timestamp of first added element - used in 'transcription' and 'merging' tasks
   * @type {import('vue').Ref<string | null>}
   */
  const firstLineTimestampEnter = ref(null)

  /**
   * timestamp of last added element - used in 'transcription' and 'merging' tasks
   * @type {import('vue').Ref<string | null>}
   */
  const lastLineTimestampEnter = ref(null)

  /**
   * previous and next transcription part annotations - used in 'transcription' task
   */
  const previousTranscriptionAnnotation = ref({
    annotationTitle: null,
    previousAnnotationTitle: null,
    caseSerialNumber: null,
    subDocketCode: null,
    caseInsertTypeTitle: null,
    caseEndTypeTitle: null
  })
  const nextTranscriptionAnnotation = ref({
    annotationTitle: null,
    nextAnnotationTitle: null,
    caseSerialNumber: null,
    subDocketCode: null,
    caseInsertTypeTitle: null,
    caseEndTypeTitle: null
  })

  /**
   * Interval ID for task ownership check
   * @type {import('vue').Ref<number | null>}
   */
  const taskOwnershipCheckInterval = ref(null)

  // * =========================== Getters ===========================

  // selected tap
  const tapStore = useTapStore()
  const { fetchTask } = tapStore

  /**
   * Selected TAP ID.
   * @type {import('vue').ComputedRef<number>}
   */
  const tapId = computed(() => tapStore.selectedTap.tapId)

  // task type and id from route params
  const route = useRoute()

  /**
   * Computed property for task type from route params.
   * @type {import('vue').ComputedRef<'readonly' | 'transcription' | 'merging' | 'approval'>}
   */
  const taskType = computed(() => route.params.taskType)

  /**
   * Computed property for task ID from route params.
   * @type {import('vue').ComputedRef<number>}
   */
  const taskId = computed(() => route.params.taskId)

  // * =========================== Actions ===========================

  const initTranscriptionOrMergingTask = async () => {
    // Fetch first available task and setup its data
    const { isSuccess, data, notFound } = await fetchTask()

    if (!isSuccess) {
      return {
        isSuccess: false,
        notFound
      }
    }

    taskData.value = await setupTaskData(data)

    if (taskData.value.taskTypeValue === 'TRANSCRIPTION') {
      const { previousAnnotationData, nextAnnotationData } =
        await fetchPreviousAndNextTranscriptionAnnotations({
          taskData: data,
          docketEntries: taskData.value.courtSession.docket.docketEntries
        })
      previousTranscriptionAnnotation.value = previousAnnotationData
      nextTranscriptionAnnotation.value = nextAnnotationData
    }

    // fetch audio path
    audioPath.value = await fetchAudioPath(data.courtSession.id)

    // set hasOpenTask flag
    hasOpenTask.value = true

    save()

    // start task ownership check
    startTaskOwnershipCheck()

    return {
      isSuccess: true,
      data: taskData.value
    }
  }

  // TODO: init correction task
  const initCorrectionTask = async () => {}

  const initApprovalTask = async () => {
    try {
      // use route params tapId and taskId to fetch data data
      const { data } = await axios(`/tasks/tap/${tapId.value}/getApprovalTask`, {
        params: {
          approvalTaskId: taskId.value
        }
      })

      taskData.value = await setupTaskData(data)

      // fetch audio path
      audioPath.value = await fetchAudioPath(data.courtSession.id)
    } catch (error) {
      console.log('🚀 ~ initApprovalTask ~ error:', error)
      return {
        isSuccess: false,
        error
      }
    }

    // set hasOpenTask flag
    hasOpenTask.value = true

    save()

    return {
      isSuccess: true
    }
  }

  const initReadonlyTask = async (courtSession) => {
    try {
      // * readonly tasks fetch only annotations
      // get docket with docketEntries
      courtSession.docket = await fetchDocket(courtSession.docket.id)

      // get court session annotations
      const courtSessionElements = await fetchCourtSessionAnnotations(courtSession.id)

      taskData.value = {
        courtSession,
        jsonText: { courtSessionElements }
      }

      // fetch audio path
      audioPath.value = await fetchAudioPath(courtSession.id)

      // set hasOpenTask flag
      hasOpenTask.value = true

      return { isSuccess: true }
    } catch (error) {
      console.log('🚀 ~ initApprovalTask ~ error:', error)
      return {
        isSuccess: false,
        error
      }
    }
  }

  const setupTaskData = async (taskData) => {
    // create a deep copy to not mutate the original task data
    const data = JSON.parse(JSON.stringify(taskData))

    // fetch docket data
    data.courtSession.docket = await fetchDocket(data.courtSession.docket.id)

    // Fetch annotations on empty transcription task
    if (data.taskTypeValue === 'TRANSCRIPTION' && !data.jsonText.courtSessionElements) {
      try {
        const { data: taskAnnotationData } = await axios.post(
          `/annotations/tap/${tapId.value}/getTaskAnnotations`,
          data
        )
        const convertedElements = convertAnnotationsToCourtSessionElements({
          taskData: data,
          taskAnnotationData
        })
        validateJsonTextElements(convertedElements)
        data.jsonText.courtSessionElements = convertedElements
      } catch (error) {
        if (error instanceof z.ZodError) {
          // Zod provides detailed error information
          console.error('Validation errors:', error.errors)
        } else {
          console.error('Error fetching task annotations:', error)
        }
      }
    }

    if (!data.jsonText.courtSessionElements) data.jsonText.courtSessionElements = []

    // get merge points
    if (data.taskTypeValue === 'MERGING') {
      mergePoints.value = data.jsonText.courtSessionElements.filter(
        ({ element }) => element === 'mergePoint'
      )
    }

    // filter invalid elements
    data.jsonText.courtSessionElements = data.jsonText.courtSessionElements.filter(
      ({ id, tag }) => id && tag !== 'p'
    )

    return data
  }

  const convertAnnotationsToCourtSessionElements = ({ taskData, taskAnnotationData }) => {
    return taskAnnotationData.map((annotation, index) => {
      if (annotation.tag === 'p') return annotation

      // Base properties common to all annotation types
      const baseObject = {
        tag: 'div',
        taskNextStartTime: 0,
        taskId: taskData.id,
        snInTask: index,
        id: `annot-${index}_${taskData.id}+0`,
        annotationTitle: annotation.jsonText.annotationTitle,
        creatorId: annotation.jsonText.creatorId,
        element: annotation.jsonText.element,
        timePositionSeconds: annotation.jsonText.timePositionSeconds,
        timePositionHour: annotation.jsonText.timePositionHour,
        timePositionMinute: annotation.jsonText.timePositionMinute,
        timePositionSecond: annotation.jsonText.timePositionSecond,
        timePosition: annotation.jsonText.timePosition,
        creationTimeStamp:
          annotation.jsonText.creationTimestamp ||
          dayjs(new Date(annotation.creationTimestamp)).format('DD/MM/YYYY HH:mm:ss')
      }

      if (!isNaN(annotation.taskNextStartTime)) {
        baseObject.taskNextStartTime = Number(annotation.taskNextStartTime)
      }

      // Add case-specific properties if this is a case-related annotation
      if (annotation.caseUid) {
        const docketEntryData = taskData.courtSession.docket.docketEntries.find(
          ({ uid }) => uid === annotation.caseUid
        )

        Object.assign(baseObject, {
          caseUid: annotation.caseUid,
          caseSerialNumber: docketEntryData.serialNumber,
          caseInsertTypeTitle: docketEntryData.insertTypeValue,
          caseInsertType: docketEntryData.insertTypeId,
          subDocketCode: docketEntryData.subCode,
          eak: docketEntryData.eak ?? null,
          gak: docketEntryData.gak ?? null,
          defendants: [],
          claimants: []
        })

        // caseEndTitle logic
        if (annotation.typeValue === 'CASE_END') {
          baseObject.caseEndType = annotation.caseEndTypeId
          baseObject.caseEndTypeTitle = getCaseEndTypeById(annotation.caseEndTypeId).value
        }

        // claimants logic
        if (Array.isArray(docketEntryData.claimants) && docketEntryData.claimants.length > 0) {
          // check if claimants is an array of strings
          if (docketEntryData.claimants.some((item) => typeof item === 'string')) {
            baseObject.claimants = docketEntryData.claimants
          } else {
            // claimants as an array of objects
            // map to array of strings
            baseObject.claimants = docketEntryData.claimants.map(({ name }) => name)
          }
        }

        // defendants logic
        if (Array.isArray(docketEntryData.defendants) && docketEntryData.defendants.length > 0) {
          // check if defendants is an array of strings
          if (docketEntryData.defendants.some((item) => typeof item === 'string')) {
            baseObject.defendants = docketEntryData.defendants
          } else {
            // defendants as an array of objects
            // map to array of strings
            baseObject.defendants = docketEntryData.defendants.map(({ name }) => name)
          }
        }
      }

      return baseObject
    })
  }

  const fetchPreviousAndNextTranscriptionAnnotations = async ({ taskData, docketEntries }) => {
    const { data: nextPreviousAnnotationsData } = await axios.post(
      `/annotations/tap/${tapId.value}/getNextPreviousAnnotations`,
      taskData
    )

    const prev = nextPreviousAnnotationsData[0]
    const prevDocketEntry = docketEntries.find(({ serialNumber }) => serialNumber === prev.text)
    const previousBaseData = {
      annotationTitle: prev.jsonText.annotationTitle ?? '-',
      caseSerialNumber: prev.text ?? '-',
      subDocketCode: prev.subDocket ?? '-',
      caseInsertTypeTitle: prevDocketEntry?.insertTypeValue ?? '-',
      previousAnnotationTitle: '-',
      caseEndTypeTitle: '-'
    }
    if (prev.typeValue === 'CASE_END') {
      previousBaseData.caseEndType = prev.typeId
      previousBaseData.caseEndTypeTitle = getCaseEndTypeById(prev.typeId).value
    }

    const next = nextPreviousAnnotationsData[1]
    const nextDocketEntry = docketEntries.find(({ serialNumber }) => serialNumber === next.text)
    const nextBaseData = {
      annotationTitle: next.jsonText.annotationTitle ?? '-',
      caseSerialNumber: next.text ?? '-',
      subDocketCode: next.subDocket ?? '-',
      caseInsertTypeTitle: nextDocketEntry?.insertTypeValue ?? '-',
      nextAnnotationTitle: '-',
      caseEndTypeTitle: '-'
    }
    if (next.typeValue === 'CASE_END') {
      nextBaseData.caseEndType = next.typeId
      nextBaseData.caseEndTypeTitle = getCaseEndTypeById(next.typeId).value
    }

    if (nextPreviousAnnotationsData[2].id) {
      previousBaseData.previousAnnotationTitle =
        nextPreviousAnnotationsData[2].jsonText.annotationTitle
    }

    if (nextPreviousAnnotationsData[3].id) {
      nextBaseData.nextAnnotationTitle = nextPreviousAnnotationsData[3].jsonText.annotationTitle
    }

    return {
      previousAnnotationData: previousBaseData,
      nextAnnotationData: nextBaseData
    }
  }

  const fetchAudioPath = async (courtSessionId) => {
    const { data } = await axios(
      `/courtSession/tap/${tapId.value}/courtSession/${courtSessionId}/getTotalAudioInformation`
    )
    return `https://crmgw.ospd.gr/wcp/stream/${data.fullPath}`
  }

  const fetchDocket = async (courtSessionId) => {
    const { data } = await axios.get(
      `/dockets/tap/${tapId.value}/docketId/${courtSessionId}/getDocket`
    )
    return data
  }

  const fetchCourtSessionAnnotations = async (courtSessionId) => {
    const { data } = await axios(`/annotations/tap/${tapId.value}/getCourtSessionAnnotations`, {
      params: {
        courtSessionId
      }
    })
    return data.map(({ text, jsonText, typeId, caseEndTypeId }) => {
      let annotationData = { ...jsonText }
      // convert text to caseSerialNumber
      if (text) {
        annotationData.caseSerialNumber = text
      }
      // convert annotation typeId to caseInsertType
      // typeId = 4 -> CaseBreak
      if (typeId < 4) {
        annotationData.caseInsertType = typeId
        annotationData.caseInsertTypeTitle = getCaseInsertTypeById(typeId).value
      }
      // convert annotation caseEndTypeId to caseEndType
      if (caseEndTypeId) {
        annotationData.caseEndType = caseEndTypeId
        annotationData.caseEndTypeTitle = getCaseEndTypeById(caseEndTypeId).value
      }

      return annotationData
    })
  }

  // const convertAnnotations

  const save = () => {
    latestSaveTime.value = formatDate(new Date(), 'hh:mm:ss')
  }

  const updateFirstAndLastTimestampEnter = () => {
    const currentTimestamp = formatDate(new Date(), 'YYYY-MM-DDTHH:mm:ss')
    if (!firstLineTimestampEnter.value) {
      firstLineTimestampEnter.value = currentTimestamp
    }
    lastLineTimestampEnter.value = currentTimestamp
  }

  // creates and adds paragraph (empty) element
  const addParagraphElement = () => {
    const pElement = createElement({ element: 'paragraph' })
    taskData.value.jsonText.courtSessionElements.push(pElement)
  }

  const convertParagraphToTranscriptionAtIndex = ({ event, index }) => {
    if (
      typeof index != 'number' ||
      index < 0 ||
      index > taskData.value.jsonText.courtSessionElements.length
    ) {
      console.warn('task.js - convertParagraphToTranscriptionAtIndex(): invalid index')
      return
    }

    if (!event || !event.data || typeof event.data != 'string') {
      console.warn('task.js - convertParagraphToTranscriptionAtIndex(): invalid event')
      return
    }

    if (taskData.value.jsonText.courtSessionElements[index].element != 'paragraph') {
      console.warn(
        `task.js - convertParagraphToTranscriptionAtIndex(): invalid element at index ${index}`
      )
      return
    }

    const { currentTime } = useAudioPlayer()
    const timePositionSeconds = Math.floor(currentTime.value | 0)
    const el = createElement({
      element: 'transcription',
      taskId: taskData.value.id,
      timePositionSeconds,
      text: event.data
    })

    console.log()

    taskData.value.jsonText.courtSessionElements[index] = el
  }

  const addTranscriptionElementAtIndex = (index) => {
    if (
      typeof index != 'number' ||
      index < 0 ||
      index > taskData.value.jsonText.courtSessionElements.length
    ) {
      console.warn('task.js - addTranscriptionElementAtIndex(): invalid index')
      return
    }

    updateFirstAndLastTimestampEnter()

    const { currentTime } = useAudioPlayer()
    const timePositionSeconds = Math.floor(currentTime.value | 0)

    // Create element
    const newElement = createElement({
      element: 'transcription',
      timePositionSeconds,
      taskId: taskData.value.id
    })

    // Insert element
    taskData.value.jsonText.courtSessionElements.splice(index, 0, newElement)

    return newElement
  }

  const addAnnotationElementAtIndex = ({ index, annotationElementData }) => {
    if (
      typeof index != 'number' ||
      index < 0 ||
      index > taskData.value.jsonText.courtSessionElements.length
    ) {
      console.warn('task.js - addTranscriptionElementAtIndex(): invalid index')
      return
    }

    updateFirstAndLastTimestampEnter()

    const { currentTime } = useAudioPlayer()
    const timePositionSeconds = Math.floor(currentTime.value | 0)

    // Create element
    const newElement = createElement({
      ...annotationElementData,
      timePositionSeconds,
      taskId: taskData.value.id
    })

    taskData.value.jsonText.courtSessionElements.splice(index, 0, newElement)

    return newElement
  }

  const editElement = (elementData) => {
    const index = taskData.value.jsonText.courtSessionElements.findIndex(
      (item) => item.id === elementData.id
    )
    taskData.value.jsonText.courtSessionElements[index] = elementData
  }

  const updateDocketEntries = (docketEntries) => {
    taskData.value.courtSession.docket.docketEntries = docketEntries
    //update each annotation
    taskData.value.jsonText.courtSessionElements.forEach((element) => {
      const elementDocketEntry = docketEntries.find(
        (docketEntry) => element.caseSerialNumber === docketEntry.serialNumber
      )
      if (!elementDocketEntry) return

      element.abm = elementDocketEntry.abm
      element.caseEndTypeTitle = elementDocketEntry.endTypeValue
      element.caseEndType = elementDocketEntry.endTypeId
      element.caseInsertTypeTitle = elementDocketEntry.insertTypeValue
      element.caseInsertType = elementDocketEntry.insertTypeId
      element.claimants = elementDocketEntry.claimants
      element.defendants = elementDocketEntry.defendants
      element.eak = elementDocketEntry.eak
      element.extraInfo = elementDocketEntry.extraInfo
      element.gak = elementDocketEntry.code
      element.subDocketCode = elementDocketEntry.subCode

      // TODO: check 'abm', 'code', 'gak', 'eak' in Civil / Criminal
      // TODO: check docketEntry 'caseCategory' equivalent property on annotation element
      // TODO: check docketEntry 'caseEndType' === element 'caseEndTypeTitle'
      // TODO: check element equivalent property of docketEntry 'extraInfo'
      // TODO: check for update in secretary or judge<template>
    })
  }

  const removeElementAtIndex = (index) => {
    taskData.value.jsonText.courtSessionElements.splice(index, 1)
  }

  const removeElementById = (elementId) => {
    const index = taskData.value.jsonText.courtSessionElements.findIndex(
      (item) => item.id === elementId
    )
    taskData.value.jsonText.courtSessionElements.splice(index, 1)
  }

  const editDocketEntry = async ({ docketEntry, key, value }) => {
    taskData.value.courtSession.docket.docketEntries.map((entry) =>
      entry.id === docketEntry.id ? { ...entry, [key]: value } : entry
    )

    // await axios.post(`dockets/tap/${tapId.value}/setDocket`, taskData.value.courtSession.docket)
  }

  // * api update
  const updateTask = async () => {
    try {
      await axios.post(`/tasks/tap/${tapId.value}/updateTask`, taskData.value)

      latestSaveTime.value = formatDate(new Date(), 'hh:mm:ss')

      return {
        isSuccess: true
      }
    } catch {
      return {
        isSuccess: false
      }
    }
  }

  const abortTask = async () => {
    try {
      const params = new URLSearchParams()
      params.append('firstLineTimestamp', firstLineTimestampEnter.value ?? 'null')
      params.append('lastLineTimestamp', lastLineTimestampEnter.value ?? 'null')

      await axios.post(`/tasks/tap/${tapId.value}/abortTask?${params.toString()}`, taskData.value)
      return {
        isSuccess: true
      }
    } catch (err) {
      console.log(err)
      return {
        isSuccess: false
      }
    }
  }

  const createCorrectionTask = async () => {
    try {
      await axios.post(`/tasks/tap/${tapId.value}/createCorrectionTask`, taskData.value)
      return {
        isSuccess: true
      }
    } catch {
      return {
        isSuccess: false
      }
    }
  }

  const validateAnnotations = async () => {
    try {
      if (taskType.value === 'transcription') {
        // const { data } = await axios.post(
        //   `/tasks/tap/${tapStore.selectedTap.tapId}/validateTaskTranscriptionEvents`,
        //   taskData.value
        // )
        // validationErrors.value = data
        validationErrors.value = []
      } else {
        const { data } = await axios.post(`/annotations/tap/validateAnnotations`, {
          jsonText: taskData.value.jsonText,
          docket: taskData.value.courtSession.docket,
          pinak: true,
          timeCheck: false,
          isApproval: true
        })
        validationErrors.value = data
      }
      if (validationErrors.value.length === 0) {
        return { isSuccess: true }
      } else {
        return { isSuccess: false }
      }
    } catch (error) {
      console.log(error)
      return { isSuccess: false }
    }
  }

  const approveTask = async () => {
    try {
      await axios.post(`/tasks/tap/${tapId.value}/approveTask`, taskData.value)
      return {
        isSuccess: true
      }
    } catch {
      return {
        isSuccess: false
      }
    }
  }

  const finishTask = async () => {
    try {
      const params = new URLSearchParams()
      params.append('firstLineTimestamp', firstLineTimestampEnter.value ?? 'null')
      params.append('lastLineTimestamp', lastLineTimestampEnter.value ?? 'null')

      await axios.post(`/tasks/tap/${tapId.value}/finishTask?${params.toString()}`, taskData.value)
      return {
        isSuccess: true
      }
    } catch {
      return {
        isSuccess: false
      }
    }
  }

  /**
   * Resets the task store.
   */
  const reset = () => {
    hasOpenTask.value = false
    taskData.value = {
      id: null,
      courtSession: {
        docket: {
          courtNameValue: null,
          building: null,
          room: null,
          judge: null,
          secretary: null,
          docketEntries: []
        },
        micAssignments: [],
        startAbsoluteTime: null
      },
      jsonText: { courtSessionElements: [] },
      audioStartTime: null,
      audioEndTime: null
    }
    audioPath.value = null
    latestSaveTime.value = null
    firstLineTimestampEnter.value = null
    lastLineTimestampEnter.value = null
    validationErrors.value = []
    mergePoints.value = []
    isTaskOwnedByUser.value = 'pending'
    previousTranscriptionAnnotation.value = {
      annotationTitle: null,
      caseSerialNumber: null,
      subDocketCode: null,
      caseInsertTypeTitle: null,
      caseEndTypeTitle: null
    }
    nextTranscriptionAnnotation.value = {
      annotationTitle: null,
      caseSerialNumber: null,
      subDocketCode: null,
      caseInsertTypeTitle: null,
      caseEndTypeTitle: null
    }
    // stop task ownership check
    stopTaskOwnershipCheck()
  }

  /**
   * Check if the current task is owned by the current user
   * @returns {Promise<string>}
   */
  const checkTaskOwnership = async () => {
    try {
      const { data } = await axios.post(`/tasks/tap/${tapId.value}/isTaskMine`, taskData.value)
      console.log('checkTaskOwnership', data)
      isTaskOwnedByUser.value = data ? 'owned' : 'not_owned'
      return isTaskOwnedByUser.value
    } catch (error) {
      console.error('Error checking task ownership:', error)
      isTaskOwnedByUser.value = 'error'
      return 'error'
    }
  }

  /**
   * Start periodic task ownership check
   * @param {number} intervalMs - Interval in milliseconds (default: 60000 - 1 minute)
   */
  const startTaskOwnershipCheck = (intervalMs = 60000) => {
    console.log('startTaskOwnershipCheck')
    // Clear any existing interval
    if (taskOwnershipCheckInterval.value) {
      clearInterval(taskOwnershipCheckInterval.value)
    }

    // Set initial status to pending
    isTaskOwnedByUser.value = 'pending'

    // Initial check
    checkTaskOwnership()

    // Set up periodic check
    taskOwnershipCheckInterval.value = setInterval(checkTaskOwnership, intervalMs)
  }

  /**
   * Stop periodic task ownership check
   */
  const stopTaskOwnershipCheck = () => {
    console.log('stopTaskOwnershipCheck')
    if (taskOwnershipCheckInterval.value) {
      clearInterval(taskOwnershipCheckInterval.value)
      taskOwnershipCheckInterval.value = null
    }
  }

  return {
    initTranscriptionOrMergingTask,
    initCorrectionTask,
    initApprovalTask,
    initReadonlyTask,
    hasOpenTask,
    taskData,
    isTaskOwnedByUser,
    previousTranscriptionAnnotation,
    nextTranscriptionAnnotation,
    audioPath,
    latestSaveTime,
    addParagraphElement,
    addTranscriptionElementAtIndex,
    addAnnotationElementAtIndex,
    convertParagraphToTranscriptionAtIndex,
    editElement,
    removeElementById,
    removeElementAtIndex,
    updateTask,
    editDocketEntry,
    updateDocketEntries,
    createCorrectionTask,
    abortTask,
    validateAnnotations,
    validationErrors,
    mergePoints,
    approveTask,
    finishTask,
    checkTaskOwnership,
    startTaskOwnershipCheck,
    stopTaskOwnershipCheck,
    reset
  }
})
